import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router'

type RefMap = Record<string, HTMLElement | null> | HTMLElement[]

interface UseScrollToJumpmarksProps {
  refArray: React.MutableRefObject<RefMap> | RefMap
  maxAttempts?: number
  attemptDelayMs?: number
  // Extra dependencies that re-trigger the scroll once async content (and its refs)
  // becomes available - e.g. a page that lazy-loads its sections after navigation.
  deps?: unknown[]
}

export const useScrollToJumpmarks = ({
  refArray,
  maxAttempts = 5,
  attemptDelayMs = 150,
  deps = [],
}: UseScrollToJumpmarksProps) => {
  const location = useLocation()
  const hasStabilizedInitialHash = useRef(false)
  const userInterruptedRef = useRef(false)
  const isProgrammaticScrollRef = useRef(false)
  const prevHashRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    const hash = location.hash
    if (!hash) return

    const targetId = hash.slice(1)
    if (!targetId) return

    const hashChanged = hash !== prevHashRef.current
    prevHashRef.current = hash

    // Prevent re-scroll when refArray changes (e.g. on re-render after user interaction)
    // but the hash itself hasn't changed and the page has already stabilized
    if (!hashChanged && hasStabilizedInitialHash.current) return

    let attempts = 0
    let timeout: ReturnType<typeof setTimeout> | undefined

    userInterruptedRef.current = false

    // When user opens url with an anchor, there could be components on the page that load data async and adjust their height after loading
    // -> On first hash, try multiple times to get the right scroll position, then only once as the page is loaded
    const triesForThisHash = hasStabilizedInitialHash.current ? 1 : maxAttempts

    const resolveElement = (): HTMLElement | null => {
      // refArray can be either a useRef object or a collection directly
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const value = (refArray as any).current ?? refArray
      if (!value) return null

      if (Array.isArray(value)) {
        return (value as HTMLElement[]).find(el => el && el.id === targetId) ?? null
      }

      const map = value as Record<string, HTMLElement | null>

      if (map[targetId]) return map[targetId]!

      const found = Object.values(map).find(el => el && el.id === targetId)
      return found ?? null
    }

    const tryScroll = () => {
      if (userInterruptedRef.current) {
        hasStabilizedInitialHash.current = true
        return
      }

      const elementToScrollTo = resolveElement()

      if (elementToScrollTo) {
        isProgrammaticScrollRef.current = true
        elementToScrollTo.scrollIntoView({
          behavior: 'smooth',
        })
      }

      attempts++
      if (attempts < triesForThisHash && !userInterruptedRef.current) {
        timeout = setTimeout(tryScroll, attemptDelayMs)
      } else if (elementToScrollTo) {
        // Only treat the hash as stabilized once the target was actually found and
        // scrolled to. If it never appeared (async content still loading), leave it
        // unstabilized so a later re-run (via `deps`) can retry once the refs exist.
        hasStabilizedInitialHash.current = true
      }
    }

    const onScroll = () => {
      if (isProgrammaticScrollRef.current) {
        isProgrammaticScrollRef.current = false
        return
      }

      userInterruptedRef.current = true
      hasStabilizedInitialHash.current = true
      if (timeout) {
        clearTimeout(timeout)
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true })

    tryScroll()

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
      window.removeEventListener('scroll', onScroll)
    }
  }, [location.hash, refArray, maxAttempts, attemptDelayMs, ...deps])
}

export default useScrollToJumpmarks
